import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useActiveLocationTimezone } from "@/hooks/use-locations";
import { dayjs } from "@/utils/date";
import { useOccupiedOverrideStore } from "@/store/occupied-override-store";

/** Renders an AlertDialog when the backend returns
 *  ``409 { code: "table_occupied_override_required" }`` while moving a
 *  SEATED party onto a table that is occupied by an overlapping
 *  reservation. The operator either moves anyway (accepting the
 *  overlap), shortens the moved party's stay so it clears before the
 *  clash, or cancels. */
export function OccupiedOverrideDialog() {
  const pending = useOccupiedOverrideStore((s) => s.pending);
  const close = useOccupiedOverrideStore((s) => s.close);
  const tz = useActiveLocationTimezone();

  const ctx = pending?.context;
  const conflicts = ctx?.conflicts ?? [];
  const canShorten = ctx?.can_shorten ?? false;
  const shortenTo = ctx?.shorten_to ?? null;

  const fmt = (iso: string) => (tz ? dayjs(iso).tz(tz) : dayjs(iso)).format("h:mm A");

  return (
    <AlertDialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open) close(null);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Mesa ocupada más tarde</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block">
              {conflicts.length === 1
                ? "La mesa elegida tiene otra reserva que se cruza:"
                : `La mesa elegida tiene ${conflicts.length} reservas que se cruzan:`}
            </span>
            <ul className="mt-2 flex flex-col gap-1 text-foreground">
              {conflicts.map((c) => (
                <li key={c.reservation_id} className="text-sm leading-snug">
                  La mesa <strong>{c.table_names.join(", ")}</strong> tiene a{" "}
                  <strong>{c.guest_name}</strong> de {fmt(c.starts_at)}–{fmt(c.ends_at)}.
                </li>
              ))}
            </ul>
            <span className="mt-3 block text-xs text-muted-foreground">
              Puedes mover igual y aceptar el cruce, acortar la estadía, o cancelar.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => close(null)}>Cancelar</AlertDialogCancel>
          <AlertDialogAction disabled={!canShorten} onClick={() => close("shorten")}>
            {canShorten && shortenTo ? `Acortar hasta ${fmt(shortenTo)}` : "Acortar la estadía"}
          </AlertDialogAction>
          <AlertDialogAction variant="destructive" onClick={() => close("force")}>
            Mover igual
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
